import { Create, Edit, useForm } from '@refinedev/antd'
import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import {
  Form,
  Input,
  Select,
  Card,
  Row,
  Col,
  Tabs,
  DatePicker,
  Upload,
  Button,
  Space,
  Image,
  message,
} from 'antd'
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { BlogEditor, type BlogEditorValue } from '@/components/blog'
import { uploadBlogImage, ACCEPTED_IMAGE_TYPES } from '@/components/blog/uploadBlogImage'
import { slugify, STATUS_META, type BlogPostStatus } from './utils'

const EMPTY: BlogEditorValue = { json: null, html: '' }

/** Minimal cover-image field: upload → store public URL string. */
function CoverUpload({ value, onChange }: { value?: string; onChange?: (v?: string) => void }) {
  const [uploading, setUploading] = useState(false)
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {value && (
        <Image src={value} alt="cover" style={{ maxHeight: 160, borderRadius: 8 }} />
      )}
      <Space>
        <Upload
          accept={ACCEPTED_IMAGE_TYPES.join(',')}
          showUploadList={false}
          beforeUpload={async (file) => {
            setUploading(true)
            try {
              const url = await uploadBlogImage(file)
              onChange?.(url)
            } catch (e) {
              message.error(e instanceof Error ? e.message : 'Otpremanje nije uspelo')
            } finally {
              setUploading(false)
            }
            return false
          }}
        >
          <Button icon={<UploadOutlined />} loading={uploading}>
            {value ? 'Promeni sliku' : 'Otpremi naslovnu sliku'}
          </Button>
        </Upload>
        {value && (
          <Button danger icon={<DeleteOutlined />} onClick={() => onChange?.(undefined)}>
            Ukloni
          </Button>
        )}
      </Space>
    </Space>
  )
}

export function BlogFormPage() {
  const { id } = useParams()
  const isEdit = Boolean(id)

  const { formProps, saveButtonProps, query, onFinish } = useForm({
    resource: 'blog',
    action: isEdit ? 'edit' : 'create',
    id,
    redirect: 'list',
  })

  const [contentSr, setContentSr] = useState<BlogEditorValue>(EMPTY)
  const [contentEn, setContentEn] = useState<BlogEditorValue>(EMPTY)

  // Seed editors from the loaded record on edit.
  const record = query?.data?.data as Record<string, any> | undefined
  useEffect(() => {
    if (!record) return
    setContentSr({ json: record.contentSr ?? null, html: record.contentHtmlSr ?? '' })
    setContentEn({ json: record.contentEn ?? null, html: record.contentHtmlEn ?? '' })
  }, [record?.id])

  const handleFinish = (values: any) => {
    const payload = {
      ...values,
      slugSr: values.slugSr?.trim() || slugify(values.titleSr || ''),
      slugEn:
        values.slugEn?.trim() || (values.titleEn ? slugify(values.titleEn) : undefined),
      contentSr: contentSr.json ?? undefined,
      contentHtmlSr: contentSr.html,
      contentEn: contentEn.json ?? undefined,
      contentHtmlEn: contentEn.html,
    }
    return onFinish(payload)
  }

  const Wrapper = isEdit ? Edit : Create

  return (
    <Wrapper
      title={isEdit ? 'Izmeni članak' : 'Novi članak'}
      saveButtonProps={saveButtonProps}
    >
      <Form
        {...formProps}
        onFinish={handleFinish}
        layout="vertical"
        initialValues={{ status: 'DRAFT', ...formProps.initialValues }}
      >
        <Row gutter={16}>
          <Col xs={24} lg={16}>
            <Card title="Sadržaj" style={{ marginBottom: 16 }}>
              <Tabs
                items={[
                  {
                    key: 'sr',
                    label: '🇷🇸 Srpski',
                    forceRender: true,
                    children: (
                      <>
                        <Form.Item
                          label="Naslov"
                          name="titleSr"
                          rules={[{ required: true, message: 'Naslov je obavezan' }]}
                        >
                          <Input placeholder="Šta je TRT terapija?" />
                        </Form.Item>
                        <Form.Item
                          label="Slug (URL)"
                          name="slugSr"
                          extra="Ostavite prazno da se generiše iz naslova"
                        >
                          <Input placeholder="sta-je-trt-terapija" addonBefore="/blog/" />
                        </Form.Item>
                        <Form.Item label="Kratak opis" name="excerptSr">
                          <Input.TextArea rows={2} placeholder="Uvodni pasus za listu i deljenje" />
                        </Form.Item>
                        <Form.Item label="Tekst">
                          <BlogEditor value={contentSr} onChange={setContentSr} />
                        </Form.Item>
                      </>
                    ),
                  },
                  {
                    key: 'en',
                    label: '🇬🇧 English',
                    forceRender: true,
                    children: (
                      <>
                        <Form.Item label="Title" name="titleEn">
                          <Input placeholder="What is TRT therapy?" />
                        </Form.Item>
                        <Form.Item
                          label="Slug (URL)"
                          name="slugEn"
                          extra="Leave empty to generate from the English title"
                        >
                          <Input placeholder="what-is-trt-therapy" addonBefore="/blog/" />
                        </Form.Item>
                        <Form.Item label="Excerpt" name="excerptEn">
                          <Input.TextArea rows={2} placeholder="Intro paragraph" />
                        </Form.Item>
                        <Form.Item label="Body">
                          <BlogEditor value={contentEn} onChange={setContentEn} placeholder="Start writing…" />
                        </Form.Item>
                      </>
                    ),
                  },
                ]}
              />
            </Card>

            <Card title="SEO (opciono)" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label="SEO naslov (SR)" name="seoTitleSr">
                    <Input />
                  </Form.Item>
                  <Form.Item label="SEO opis (SR)" name="seoDescSr">
                    <Input.TextArea rows={2} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="SEO title (EN)" name="seoTitleEn">
                    <Input />
                  </Form.Item>
                  <Form.Item label="SEO description (EN)" name="seoDescEn">
                    <Input.TextArea rows={2} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card title="Objava" style={{ marginBottom: 16 }}>
              <Form.Item label="Status" name="status">
                <Select
                  options={Object.entries(STATUS_META).map(([value, meta]) => ({
                    value,
                    label: meta.label,
                  }))}
                />
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prev, cur) => prev.status !== cur.status}
              >
                {({ getFieldValue }) =>
                  (getFieldValue('status') as BlogPostStatus) === 'SCHEDULED' ? (
                    <Form.Item
                      label="Datum objave"
                      name="scheduledAt"
                      getValueProps={(value) => ({ value: value ? dayjs(value) : undefined })}
                      getValueFromEvent={(date) => date?.toISOString()}
                    >
                      <DatePicker showTime style={{ width: '100%' }} format="DD.MM.YYYY HH:mm" />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>
            </Card>

            <Card title="Meta" style={{ marginBottom: 16 }}>
              <Form.Item label="Kategorija" name="category">
                <Input placeholder="npr. Edukacija" />
              </Form.Item>
              <Form.Item label="Tagovi" name="tags">
                <Select mode="tags" placeholder="Dodaj tagove" tokenSeparators={[',']} />
              </Form.Item>
            </Card>

            <Card title="Naslovna slika">
              <Form.Item name="coverImageUrl" noStyle>
                <CoverUpload />
              </Form.Item>
            </Card>
          </Col>
        </Row>
      </Form>
    </Wrapper>
  )
}

export default BlogFormPage
